/**
 * DocumentViewerScreen — the REUSABLE inline PDF viewer (dms/spec.md §4.5,
 * §M11 — "this is the centerpiece; M12 will REUSE it"). Route: `/ablage/$id`.
 *
 * Opens in the SAME tab (a route, not `window.open`/a new tab). Left = the PDF
 * rendered inline via `GET /documents/:id/content`; right = "Verknüpfte
 * Objekte" — clickable cards for Dokument (implicit) · Kunde · Police(+type) ·
 * Aufgabe×N, built from `DocumentDetailDto.connected_objects` (server-resolved
 * `route`s, same posture as `SearchHit.route` — no client-side routing rules).
 *
 * Auth-gated bytes ⇒ the "blob detour" (fetch with the bearer, wrap in an object
 * URL) — a plain `<iframe src="/api/documents/:id/content">` would never send the
 * token (same reasoning as `MandateWizardDialog.documentBlob`). Mock mode skips the
 * fetch and points the iframe straight at `detail.content_url` (no auth, no real
 * bytes needed to exercise the UI in tests/dev). Both legs live in
 * `useDocumentBlobUrl` so the Bearbeitungsmaske's Eingang pane shares ONE
 * implementation instead of a second copy.
 *
 * ── "Back to this exact viewer" ──────────────────────────────────────────
 * Per dms/spec.md §4.5: "Clicking a card navigates to that object's page;
 * provide a one-click Back that returns to THIS exact viewer... Implement
 * with router state so back-nav restores the viewer, not the browser history
 * default." AppShell ALREADY provides exactly this — a global topbar "Zurück"
 * wired to `router.history.back()` ("Kap. 25: REAL history navigation — back
 * restores the previous URL incl. search params; no state resets", every
 * connected-object destination (/customers/$id, /policen/$id, /aufgaben) is a
 * sibling route under the SAME `appLayoutRoute` and therefore renders inside
 * the SAME AppShell). So a card click here does a plain PUSH navigation
 * (`navigate({ to: card.route })`, never `replace: true`) and the existing
 * global Zurück button is the "one-click Back … position flexible" control —
 * no per-destination-page changes needed, and every future connected-object
 * type gets it for free.
 *
 * [Source: apps/web/src/app/shell/AppShell.tsx] (the "Zurück" → router.history.back())
 * [Source: apps/web/src/features/search/GlobalSearchDialog.tsx] (`navigate({ to: hit.route })` precedent)
 * [Source: apps/web/src/features/mandate/MandateWizardDialog.tsx] (blob-detour precedent)
 * [Source: projects/broker-ag/dms/spec.md §4.5, §M11]
 */
import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  ChevronRight,
  Download,
  FileText,
  ListTodo,
  MessageSquare,
  Pencil,
  ReceiptText,
  ShieldCheck,
  X,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { DocumentObjectType } from '@brokly/shared';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { StatusDot } from '../../components/ui/StatusDot';
import { Toast } from '../../components/ui/Toast';
import { dateShortDe } from '../../lib/format';
import { saveBlob } from '../../lib/save-blob';
import { apiClient } from '../../lib/api-client';
import type { DocumentUpdateBody } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useDocumentDetail } from './hooks/useDocumentDetail';
import { usePdfObjectUrl } from './hooks/usePdfObjectUrl';
import { DocumentEditDialog, type LinkTargetOption } from './components/DocumentEditDialog';
import { resolveTitle, sizeDisplay } from './api/documents-api';

const CONNECTED_ICON: Record<DocumentObjectType, ComponentType<{ className?: string }>> = {
  firma: Building2,
  person: Building2,
  policy: ShieldCheck,
  task: ListTodo,
  communication: MessageSquare,
  mandate: FileText,
  claim: AlertTriangle,
  invoice: ReceiptText,
  versicherer: Building2,
};

const CONNECTED_LABEL: Record<DocumentObjectType, string> = {
  firma: 'Kunde',
  person: 'Kunde',
  policy: 'Police',
  task: 'Aufgabe',
  communication: 'Kommunikation',
  mandate: 'Mandat',
  claim: 'Schaden',
  invoice: 'Rechnung',
  versicherer: 'Versicherer',
};

export function DocumentViewerScreen() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const live = isLiveData();
  const perms = useEffectivePermissions();
  const { detail, isLoading } = useDocumentDetail(id);

  const [editOpen, setEditOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  function showFeedback(msg: string) {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  }

  // docs-fix item 2b: live typeaheads for the reclassify dialog's linked-object picker.
  async function searchKunden(q: string): Promise<LinkTargetOption[]> {
    if (!live) return [];
    const res = await apiClient.customers.list({ q, limit: 8 });
    return res.items
      .filter((c) => c.name)
      .map((c) => ({
        linkedType: c.subject_type,
        id: c.subject_id,
        label: c.name as string,
        subtitle: [c.subject_type === 'firma' ? 'Firma' : 'Person', c.city].filter(Boolean).join(' · '),
      }));
  }
  async function searchPolicies(q: string): Promise<LinkTargetOption[]> {
    if (!live) return [];
    // GET /policies has no free-text leg — fetch + client-filter by number/Bezeichnung.
    const needle = q.toLowerCase();
    const rows = await apiClient.policies.list({});
    return rows
      .filter(
        (p) =>
          p.policy_number.toLowerCase().includes(needle) ||
          (p.bezeichnung ?? '').toLowerCase().includes(needle),
      )
      .slice(0, 8)
      .map((p) => ({
        linkedType: 'policy',
        id: p.id,
        label: p.policy_number,
        subtitle: p.bezeichnung,
      }));
  }

  async function handleEditSubmit(patch: DocumentUpdateBody) {
    if (!live) {
      showFeedback('Dokument aktualisiert.');
      return;
    }
    await apiClient.documents.update(id, patch);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['documents'] }),
      qc.invalidateQueries({ queryKey: ['policies'] }),
    ]);
    showFeedback('Dokument aktualisiert.');
  }

  // The preview src (live: auth-gated bytes → object URL; mock: content_url verbatim).
  // usePdfObjectUrl, not the raw hook: this iframe renders whatever it is handed, and an
  // object URL inherits our origin. A document whose bytes are not really a PDF now
  // shows the unavailable state instead of executing as a page on app.brokly.ch.
  const { src: pdfSrc, isError: contentError } = usePdfObjectUrl(id, detail?.content_url ?? null);

  if (isLoading) {
    return (
      <div className="p-12 text-center text-sm text-muted-foreground">Lade Dokument …</div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <p className="text-sm text-muted-foreground">Dokument nicht gefunden.</p>
        <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/ablage' })}>
          Zurück zur Ablage
        </Button>
      </div>
    );
  }

  const title = resolveTitle(detail.title, detail.filename);
  // Best-effort label of the current primary link (if it happens to also be a connected card).
  const currentLinkLabel =
    detail.connected_objects.find((c) => c.object_id === detail.linked_id)?.title ?? null;

  async function handleDownload() {
    try {
      // DL-2 fix (wave dms-fixC): the header "Herunterladen" is a genuine download —
      // `document.download` (`/download`), NOT `.content`'s `document.read` inline bytes
      // (the iframe preview above stays on `.content`, unchanged).
      const blob = await apiClient.documents.download(id);
      saveBlob(blob, title);
    } catch {
      // Header convenience action — the Ablage grid's row action is the
      // authoritative download surface; a failure here is silently ignored.
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-base font-semibold text-foreground">{title}</h1>
            {detail.state === 'active' ? (
              <StatusDot tone="success" label="Aktiv" />
            ) : (
              <StatusDot tone="neutral" label="Inaktiv" />
            )}
            {detail.version_no > 1 && <Badge variant="neutral">Version {detail.version_no}</Badge>}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {dateShortDe(detail.created_at)} · {sizeDisplay(detail.size_bytes)}
            {detail.source_label ? ` · ${detail.source_label}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {perms.can('document.update') && live && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="size-4" /> Bearbeiten
            </Button>
          )}
          {perms.can('document.download') && live && (
            <Button variant="outline" size="sm" onClick={() => void handleDownload()}>
              <Download className="size-4" /> Herunterladen
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Zurück zur Ablage"
            title="Zurück zur Ablage"
            onClick={() => void navigate({ to: '/ablage' })}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {/* Body: left PDF, right Verknüpfte Objekte */}
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_360px]">
        <div className="min-h-0 bg-muted/30">
          {pdfSrc ? (
            <iframe title={`PDF-Vorschau: ${title}`} src={pdfSrc} className="size-full border-0" />
          ) : contentError ? (
            <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
              Das Dokument ist derzeit nicht verfügbar.
            </div>
          ) : (
            <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
              Lade Vorschau …
            </div>
          )}
        </div>

        <aside className="min-h-0 overflow-y-auto border-l border-border p-4">
          <h2 className="text-sm font-semibold text-foreground">Verknüpfte Objekte</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Objekt anklicken, um zur Detailansicht zu wechseln.
          </p>

          <div className="mt-3 flex flex-col gap-2">
            {/* DOKUMENT — implicit (the document being viewed), non-interactive info row. */}
            <Card className="flex items-start gap-2.5 p-3">
              <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Dokument
                </div>
                <div className="truncate text-sm font-medium text-foreground">{title}</div>
                <div className="text-xs text-muted-foreground">{dateShortDe(detail.created_at)}</div>
              </div>
            </Card>

            {detail.connected_objects.map((c) => {
              const Icon = CONNECTED_ICON[c.object_type] ?? FileText;
              return (
                <Card
                  key={`${c.object_type}-${c.object_id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => void navigate({ to: c.route })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void navigate({ to: c.route });
                  }}
                  className="flex cursor-pointer items-start gap-2.5 p-3 hover:border-primary/50"
                >
                  <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {CONNECTED_LABEL[c.object_type] ?? c.object_type}
                    </div>
                    <div className="truncate text-sm font-medium text-foreground">{c.title}</div>
                    {c.subtitle && (
                      <div className="truncate text-xs text-muted-foreground">{c.subtitle}</div>
                    )}
                  </div>
                  <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                </Card>
              );
            })}
          </div>
        </aside>
      </div>

      {/* docs-fix item 2b: manual reclassify — change Kategorie + linked object (Kunde/Police). */}
      <DocumentEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        current={{
          id: detail.id,
          kind: detail.kind,
          linkedType: detail.linked_type,
          linkedId: detail.linked_id,
        }}
        currentLinkLabel={currentLinkLabel}
        onSubmit={handleEditSubmit}
        onSearchKunden={searchKunden}
        onSearchPolicies={searchPolicies}
      />

      {feedback && <Toast tone="ok">{feedback}</Toast>}
    </div>
  );
}
